import { Card, Divider, Stack, styled, type Theme, useTheme } from '@mui/material'
import { Percentage, decimalToLocalizedString, tokens } from 'ComponentLibrary'
import { ComputedNode } from '@nivo/treemap'
import { TrendIcon } from 'ComponentLibrary/src/molecules/TrendIcon'
import { Typography } from 'ComponentLibrary/src/piazza'
import { type TreeMapPortfolioDatum } from './types'

type TreeMapPortfolioTooltipProps = {
    node: ComputedNode<TreeMapPortfolioDatum>
    marketValueLabel?: string
    valueTrendLabel?: string
    portfolioShareLabel?: string
    formatValue?: (value: number) => string
    formatPercentage?: (value: number) => string
}

const TreeMapPortfolioTooltip = ({
    node,
    marketValueLabel = 'Marktwert',
    valueTrendLabel = 'Wertentwicklung',
    portfolioShareLabel = 'Marktwertanteil Portfolio',
    formatValue = (value) => `${decimalToLocalizedString(value, false, 2)} €`,
    formatPercentage,
}: TreeMapPortfolioTooltipProps) => {
    const { trend, labelStreetAndHouseNumber, labelCityAndReference, percentage } = node.data
    const theme = useTheme()
    const formattedValue = formatValue(node.value)
    const formattedPercentage =
        percentage !== undefined ? (formatPercentage?.(percentage) ?? new Percentage(percentage).format({ withSign: false })) : undefined

    return (
        <StyledTooltip>
            <Stack direction="column" gap={tokens.SizingSpacing050}>
                <Typography variant="textS" color={theme.palette.customtext.black54}>
                    {labelCityAndReference}
                </Typography>
                <Typography variant="textS" color={theme.palette.customtext.black54}>
                    {labelStreetAndHouseNumber}
                </Typography>
                <Divider />
                <Stack direction="row" alignItems="center" whiteSpace="nowrap" gap={theme.spacing(1)}>
                    <Typography variant="textS" color={theme.palette.customtext.black54}>
                        {marketValueLabel}:
                    </Typography>
                    <Typography variant="textS">{formattedValue}</Typography>
                </Stack>
                {trend !== undefined && (
                    <Stack direction="row" alignItems="center" whiteSpace="nowrap" gap={theme.spacing(1)}>
                        <Typography variant="textS" color={theme.palette.customtext.black54}>
                            {valueTrendLabel}:
                        </Typography>
                        <Typography variant="textS">
                            <TrendIcon trend={trend} variant="arrow" iconSize="14px" />
                        </Typography>
                    </Stack>
                )}
                {node.treeDepth > 1 && (
                    <Stack direction="row" gap={tokens.SizingSpacing050}>
                        <Typography variant="textS" color={theme.palette.customtext.black54}>
                            {portfolioShareLabel}:
                        </Typography>
                        <Typography variant="textS">{formattedPercentage}</Typography>
                    </Stack>
                )}
            </Stack>
        </StyledTooltip>
    )
}

export default TreeMapPortfolioTooltip

const StyledTooltip = styled(Card)(({ theme }: { theme: Theme }) => ({
    background: theme.palette.background.paper,
    padding: '4px',
}))
